// Command Palette global (⌘K / Ctrl+K): búsqueda de leads, contactos y cuentas,
// agrupada por tipo, con navegación por teclado. Blur de fondo + scale-in.
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, CornerDownLeft, Search, Target, Users } from "lucide-react";
import { api } from "../lib/api.ts";
import type { Row } from "../lib/resource.ts";

interface Hit { id: string; label: string; sub: string; type: "leads" | "contacts" | "accounts"; }
const TYPE = {
  leads: { icon: Target, label: "Leads", path: "leads" },
  contacts: { icon: Users, label: "Contactos", path: "contacts" },
  accounts: { icon: Building2, label: "Cuentas", path: "accounts" },
} as const;

export function CommandPalette(): JSX.Element | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Atajo global ⌘K / Ctrl+K y Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((v) => !v); }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpen = (): void => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("tiger:cmdk", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("tiger:cmdk", onOpen); };
  }, []);

  useEffect(() => { if (open) { setQ(""); setDebounced(""); setActive(0); setTimeout(() => inputRef.current?.focus(), 40); } }, [open]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 200); return () => clearTimeout(t); }, [q]);

  const search = useQuery<Hit[]>({
    queryKey: ["cmdk", debounced],
    enabled: open && debounced.length > 0,
    queryFn: async () => {
      const [le, co, ac] = await Promise.all([
        api.get<{ rows: Row[] }>(`/leads?pageSize=5&q=${encodeURIComponent(debounced)}`),
        api.get<{ rows: Row[] }>(`/contacts?pageSize=5&q=${encodeURIComponent(debounced)}`),
        api.get<{ rows: Row[] }>(`/accounts?pageSize=5&q=${encodeURIComponent(debounced)}`),
      ]);
      const name = (r: Row): string => `${String(r["firstName"] ?? "")} ${String(r["lastName"] ?? "")}`.trim();
      const out: Hit[] = [];
      for (const r of le.rows) out.push({ id: String(r["id"]), label: name(r) || "(sin nombre)", sub: String(r["company"] ?? ""), type: "leads" });
      for (const r of co.rows) out.push({ id: String(r["id"]), label: name(r) || "(sin nombre)", sub: String(r["accountName"] ?? r["email"] ?? ""), type: "contacts" });
      for (const r of ac.rows) out.push({ id: String(r["id"]), label: String(r["accountName"] ?? ""), sub: String(r["industry"] ?? ""), type: "accounts" });
      return out;
    },
  });

  const hits = useMemo(() => search.data ?? [], [search.data]);
  useEffect(() => { setActive(0); }, [hits]);

  const go = (h: Hit): void => { setOpen(false); navigate(`/${TYPE[h.type].path}/${h.id}`); };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(hits.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === "Enter" && hits[active]) { e.preventDefault(); go(hits[active]!); }
  };

  if (!open) return null;

  // Agrupación por tipo, preservando el orden.
  const groups: Array<{ type: Hit["type"]; items: Hit[] }> = [];
  for (const h of hits) {
    let g = groups.find((x) => x.type === h.type);
    if (!g) { g = { type: h.type, items: [] }; groups.push(g); }
    g.items.push(h);
  }
  let flat = -1;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4" onMouseDown={() => setOpen(false)}>
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm animate-[fade_200ms_ease-out]" />
      <div
        className="relative w-full max-w-xl card overflow-hidden animate-[pop_180ms_var(--ease-out)]"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ boxShadow: "var(--shadow-lg)" }}
      >
        <div className="flex items-center gap-3 px-4 border-b border-line">
          <Search size={18} strokeWidth={1.75} className="text-ink-muted shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar leads, contactos, cuentas…"
            className="flex-1 py-3.5 text-sm bg-transparent outline-none placeholder:text-ink-muted"
          />
          <kbd className="text-[10px] text-ink-muted border border-line rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin py-2">
          {debounced.length === 0 ? (
            <p className="px-4 py-6 text-sm text-ink-muted text-center">Escribe para buscar en toda la plataforma.</p>
          ) : search.isLoading ? (
            <p className="px-4 py-6 text-sm text-ink-muted text-center">Buscando…</p>
          ) : hits.length === 0 ? (
            <p className="px-4 py-6 text-sm text-ink-muted text-center">Sin resultados para "{debounced}".</p>
          ) : (
            groups.map((g) => {
              const T = TYPE[g.type];
              return (
                <div key={g.type} className="mb-1">
                  <div className="px-4 py-1 text-[11px] uppercase tracking-wider text-ink-muted font-medium">{T.label}</div>
                  {g.items.map((h) => {
                    flat += 1; const idx = flat; const Icon = T.icon;
                    return (
                      <button
                        key={`${h.type}-${h.id}`}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => go(h)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${idx === active ? "bg-primary-soft" : "hover:bg-surface-hover"}`}
                      >
                        <span className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${idx === active ? "bg-primary text-white" : "bg-slate-100 dark:bg-slate-800 text-ink-secondary"}`}>
                          <Icon size={15} strokeWidth={1.75} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-ink truncate">{h.label}</span>
                          {h.sub && <span className="block text-xs text-ink-muted truncate">{h.sub}</span>}
                        </span>
                        {idx === active && <CornerDownLeft size={14} className="text-ink-muted shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
